import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Link,
	Skeleton,
	Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { ORDERS_STATUS } from 'redux/reducers/orders/constants';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
	getFirstMileLabelRequest,
	getFirstMileLabelSuccess,
	getSwissPostDownloadLabelRequest,
	getSwissPostDownloadProformaRequest,
	getSwissPostOrderRequest,
	requestSwissPostDelivery,
	sendSwissPostLastErrorByEmailInit,
	sendSwissPostLastErrorByEmailRequest,
	setFirstMileLabelErrorDialog,
	uploadSwissPostProformaRequest,
} from 'redux/reducers/shipments/reducer';
import {
	getFirstMileLabelError,
	getFirstMileLabelErrorDialog,
	getFirstMileLabelIsLoad,
	getRequestDeliveryError,
	getRequestDeliveryIsLoad,
	getSendLastErrorByEmailError,
	getSendLastErrorByEmailIsLoad,
	getSendLastErrorByEmailSuccess,
	getShipmentProformaIsLoad,
	getShipmentsLabelIsLoad,
	getShipmentsOrder,
	getShipmentsOrderError,
	getShipmentsOrderIsLoad,
	uploadShipmentProformaIsLoad,
} from 'redux/reducers/shipments/selectors';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { OrderError } from 'components/Order/OrderError';
import { CloudDownload, CloudUpload, LocalShipping } from '@mui/icons-material';
import { NoticeDialog } from 'ui/NoticeDialog';
import { ErrorBanner } from 'ui/ErrorBanner';
import { PATHS } from 'routes/constants';
import { ShipmentTimeLine } from './ShipmentTimeLine';
import { ShipmentDetails } from './ShipmentDetails';
import { OrderDetails } from './OrderDetails';
import { AmountBreakdown } from './AmountBreakdown';
import { DeliveryInformation } from './DeliveryInformation';
import { ReminderAlert } from './ReminderAlert';
import { UploadProFormaDialog } from './UploadProFormaDialog';
// On-screen signing of the pro-forma is switched off for now. The dialog is kept in
// ./SignProFormaDialog; to bring it back, uncomment the "On-screen signing" blocks in this file.
// import { Draw } from '@mui/icons-material';
// import { SignProFormaDialog } from './SignProFormaDialog';

export const Shipment: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams<{ orderId?: string }>();
	const { orderId } = params;
	const order = useSelector(getShipmentsOrder);
	const orderIsLoading = useSelector(getShipmentsOrderIsLoad);
	const orderError = useSelector(getShipmentsOrderError);
	const requestDeliveryError = useSelector(getRequestDeliveryError);
	const savedUrl = useSelector(getSavedUrl);
	const downloadLabelLoader = useSelector(getShipmentsLabelIsLoad);
	const requestDeliveryLoader = useSelector(getRequestDeliveryIsLoad);
	const downloadProFormaLoader = useSelector(getShipmentProformaIsLoad);
	const uploadProFormaLoader = useSelector(uploadShipmentProformaIsLoad);
	const downloadFirstMileLabelLoader = useSelector(getFirstMileLabelIsLoad);
	const firstMileLabelErrorDialog = useSelector(getFirstMileLabelErrorDialog);
	const firstMileLabelError = useSelector(getFirstMileLabelError);
	const sendLastErrorByEmailError = useSelector(getSendLastErrorByEmailError);
	const sendLastErrorByEmailLoading = useSelector(getSendLastErrorByEmailIsLoad);
	const sendLastErrorByEmailSuccess = useSelector(getSendLastErrorByEmailSuccess);
	const downloadLoader =
		downloadLabelLoader ||
		requestDeliveryLoader ||
		downloadProFormaLoader ||
		downloadFirstMileLabelLoader ||
		sendLastErrorByEmailLoading;

	const validateFields = order?.fieldsConfig.every((field) => field.isMatchRegexp);
	const validateCustomDescription = order?.orderDetails.rows.every((row) =>
		row.children.every((child) => !!child.customsDescription),
	);

	const [requestDeliveryDialogOpen, setRequestDeliveryDialogOpen] = useState(false);
	const [reminderAlertOpen, setReminderAlertOpen] = useState(true);
	const handleCloseReminder = () => {
		setReminderAlertOpen(false);
	};

	const handleRequestDeliveryDialogClose = () => setRequestDeliveryDialogOpen(false);
	const handleRequestDeliveryDialogOpen = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		setRequestDeliveryDialogOpen(true);
		handleCloseReminder();
	};
	const handleRequestDeliveryDiscount = () => {
		if (!order) return;
		dispatch(requestSwissPostDelivery({ orderId: order.order.id }));
		setRequestDeliveryDialogOpen(false);
	};

	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const handleUploadDialogClose = () => setUploadDialogOpen(false);

	useEffect(() => {
		if (!orderId) return;
		dispatch(
			getSwissPostOrderRequest({
				orderId: Number(orderId),
			}),
		);
	}, [dispatch, orderId]);

	const handleReplayHistory = () => {
		const currentPath = window.location.pathname + window.location.search;
		if (savedUrl && savedUrl.includes(PATHS.SHIPMENTS_PAGE) && savedUrl !== currentPath) {
			navigate(savedUrl);
		} else {
			navigate(PATHS.SHIPMENTS_PAGE);
		}
	};

	const handleGoToTracking = (trackingLink: string, e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		window.open(trackingLink);
	};

	const handleDownloadLabel = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!order) return;

		dispatch(
			getSwissPostDownloadLabelRequest({
				orderId: order.order.id,
				trackingNumber: order.order.trackingNumber,
			}),
		);
	};

	const handleDownloadFirstMileLabel = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!order) return;

		dispatch(getFirstMileLabelRequest({ orderId: order.order.id }));
	};

	const handleDownloadInvoiceProforma = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!order) return;

		dispatch(
			getSwissPostDownloadProformaRequest({
				orderId: order.order.id,
				externalOrderId: order.order.externalOrderId,
			}),
		);
	};

	const handleUploadInvoiceProforma = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!params.orderId) return;
		if (!e.target.files) return;
		const formData = new FormData();
		formData.append('File', e.target.files[0]);

		dispatch(
			uploadSwissPostProformaRequest({
				orderId: +params.orderId,
				file: formData,
			}),
		);
		handleUploadDialogClose();
	};

	const handleOpenUploadProFormaDialog = () => {
		handleCloseReminder();
		setUploadDialogOpen(true);
	};

	// On-screen signing:
	// const [signDialogOpen, setSignDialogOpen] = useState(false);
	// const handleSignDialogClose = () => setSignDialogOpen(false);
	// const handleOpenSignProFormaDialog = (e: React.MouseEvent<HTMLElement>) => {
	// 	e.stopPropagation();
	// 	handleCloseReminder();
	// 	setSignDialogOpen(true);
	// };

	const [firstMileLabelErrorDialogOpen, setFirstMileLabelErrorDialogOpen] = useState(false);
	const handleFirstMileLabelErrorDialogClose = () => {
		setFirstMileLabelErrorDialogOpen(false);
		dispatch(setFirstMileLabelErrorDialog(null));
		dispatch(getFirstMileLabelSuccess());
	};
	useEffect(() => {
		if (firstMileLabelErrorDialog) {
			setFirstMileLabelErrorDialogOpen(true);
		}
	}, [firstMileLabelErrorDialog]);

	const handleSupportRequest = () => {
		if (!order) return;
		dispatch(sendSwissPostLastErrorByEmailRequest({ orderId: order.order.id }));
	};

	useEffect(() => {
		if (sendLastErrorByEmailSuccess) {
			dispatch(sendSwissPostLastErrorByEmailInit());
		}
	}, [dispatch, sendLastErrorByEmailSuccess]);

	return (
		<Box>
			{requestDeliveryError && !sendLastErrorByEmailError && !sendLastErrorByEmailSuccess && (
				<ErrorBanner
					text={requestDeliveryError}
					sx={{ mt: '-20px' }}
					buttonText="Send Support request"
					onClick={handleSupportRequest}
					buttonLoading={sendLastErrorByEmailLoading}
				/>
			)}
			{sendLastErrorByEmailSuccess && (
				<ErrorBanner
					text="Error report submitted. Our support team will contact you via email with a resolution."
					sx={{ mt: '-20px' }}
					type="warning"
					noClose={true}
				/>
			)}
			{firstMileLabelError && <ErrorBanner text={firstMileLabelError} sx={{ mt: '-20px' }} />}
			<Box sx={{ padding: '10px 20px 0 20px', maxWidth: '100vw', overflowX: 'auto' }}>
				<Box sx={{ minWidth: '800px' }}>
					{!!orderError && (
						<OrderError
							text={orderError ?? 'Order not found'}
							linkText="Go to the Shipments list"
							link="/shipments"
						/>
					)}

					{!orderError && !!order && !orderIsLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
							<Box sx={{ mb: 2 }}>
								<Box
									sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1 }}
								>
									<IconButton onClick={handleReplayHistory} color="primary">
										<KeyboardBackspaceIcon />
									</IconButton>
									<Typography variant="h5" color="primary">
										{order?.order.externalOrderId}
									</Typography>
									{order?.order.status && (
										<Chip
											label={order?.order.status?.replace('_', ' ')}
											size="small"
											sx={{
												p: '0 5px',
												ml: 1,
												fontSize: '.7rem',
												height: '20px',
												backgroundColor:
													order?.order.status === ORDERS_STATUS.PAID
														? 'success.light'
														: order?.order.status === ORDERS_STATUS.AWAITING_PAYMENT
															? 'warning.light'
															: 'primary.light',
												color: '#000000a0',
											}}
										/>
									)}
									{order?.order.shippingStatus && (
										<Chip
											label={order?.order.shippingStatus}
											size="small"
											sx={{
												p: '0 5px',
												fontSize: '.8rem',
												height: '20px',
												bgcolor:
													order?.order.shippingStatus === 'Awaiting Signing' ||
													order?.order.shippingStatus === 'Unfulfilled'
														? 'warning.light'
														: 'primary.light',
												color: '#000000a0',
											}}
										/>
									)}
								</Box>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'start',
										gap: '20px',
									}}
								>
									{order?.order.saleDateProvidedByEm && (
										<Typography variant="body2" gutterBottom color="GrayText">
											Payment date:{' '}
											{DateTime.fromISO(order?.order.saleDateProvidedByEm).toFormat(
												'yyyy-MM-dd, HH:mm',
											)}
										</Typography>
									)}
									{order?.order.reportingPaymentDate && (
										<Typography variant="body2" gutterBottom color="GrayText">
											Reporting date:{' '}
											{DateTime.fromISO(order?.order.reportingPaymentDate).toFormat(
												'yyyy-MM-dd, HH:mm',
											)}
										</Typography>
									)}
								</Box>
							</Box>
							<Box
								sx={{
									mt: 1,
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									'&': {
										'& button': {
											fontSize: '.7rem',
										},
										position: 'relative',
									},
								}}
							>
								{order?.order?.valuesStatus === 'Unprocessed' ? (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											gap: 0.5,
											alignItems: 'center',
										}}
									>
										<Typography color="error" sx={{ fontSize: '.8rem', fontWeight: 'bold' }}>
											{order?.order.deliveryPhoneValidate
												? 'Please check order information'
												: order?.order.validateHsCode
													? 'HS Codes are not valid'
													: ''}
										</Typography>
										<Box sx={{ position: 'relative' }}>
											<Button
												variant="contained"
												onClick={handleRequestDeliveryDialogOpen}
												disabled={
													downloadLoader ||
													order.order.validateHsCode ||
													!validateFields ||
													!validateCustomDescription ||
													sendLastErrorByEmailLoading ||
													sendLastErrorByEmailSuccess
												}
											>
												Request Swiss Post Delivery
												{requestDeliveryLoader && (
													<CircularProgress
														size={24}
														sx={{
															color: '#004366',
															position: 'absolute',
															top: '50%',
															left: '50%',
															marginTop: '-12px',
															marginLeft: '-12px',
														}}
													/>
												)}
											</Button>
											<ReminderAlert
												open={reminderAlertOpen}
												onClose={handleCloseReminder}
												text='Please click "Request Swiss Post Delivery" to continue'
											/>
										</Box>
									</Box>
								) : order?.order?.shippingStatus === 'Awaiting Signing' ? (
									<>
										{order?.order.schemes !== 'IOSS-IMPORT' && (
											<Button
												variant="contained"
												color="primary"
												onClick={handleDownloadInvoiceProforma}
												disabled={downloadLoader}
												startIcon={<CloudDownload />}
											>
												Pro-forma Invoice
												{downloadProFormaLoader && (
													<CircularProgress
														size={24}
														sx={{
															color: '#004366',
															position: 'absolute',
															top: '50%',
															left: '50%',
															marginTop: '-12px',
															marginLeft: '-12px',
														}}
													/>
												)}
											</Button>
										)}
										{/* On-screen signing:
										{order?.order.schemes !== 'IOSS-IMPORT' && (
											<Button
												variant="contained"
												color="primary"
												onClick={handleOpenSignProFormaDialog}
												disabled={downloadLoader}
												startIcon={<Draw />}
											>
												Sign Pro-forma Manually
											</Button>
										)} */}
										<Button
											variant="contained"
											color="primary"
											tabIndex={-1}
											startIcon={<CloudUpload />}
											disabled={downloadLoader}
											sx={{ fontSize: '.7rem' }}
											onClick={(e) => {
												e.stopPropagation();
												handleOpenUploadProFormaDialog();
											}}
										>
											{order?.order.schemes === 'IOSS-IMPORT'
												? 'Upload Invoice for IOSS order'
												: 'Upload Signed Pro-forma Invoice'}
											{uploadProFormaLoader && (
												<CircularProgress
													size={24}
													sx={{
														color: '#004366',
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button>
										<ReminderAlert
											open={reminderAlertOpen}
											onClose={handleCloseReminder}
											text={
												order?.order.schemes === 'IOSS-IMPORT'
													? 'Please, upload IOSS supportive document'
													: 'Please download the Pro-forma Invoice, sign it and upload it back'
											}
										/>
									</>
								) : (
									<>
										<Button
											variant="contained"
											color="primary"
											onClick={handleDownloadFirstMileLabel}
											disabled={downloadLoader}
											startIcon={<CloudDownload />}
										>
											First Mile Label
											{downloadFirstMileLabelLoader && (
												<CircularProgress
													size={24}
													sx={{
														color: '#004366',
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button>

										<Button
											variant="contained"
											color="primary"
											onClick={handleDownloadLabel}
											disabled={downloadLoader}
											startIcon={<CloudDownload />}
										>
											Last Mile Label
											{downloadLabelLoader && (
												<CircularProgress
													size={24}
													sx={{
														color: '#004366',
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button>
										<Button
											variant="contained"
											color="primary"
											onClick={(e) => handleGoToTracking(order?.order.tracking, e)}
											disabled={downloadLoader}
											startIcon={<LocalShipping />}
										>
											Tracking
										</Button>
										{order?.order?.schemes !== 'IOSS-IMPORT' && (
											<Button
												variant="contained"
												color="primary"
												onClick={handleDownloadInvoiceProforma}
												disabled={downloadLoader}
												startIcon={<CloudDownload />}
											>
												Signed Pro-forma Invoice
												{downloadProFormaLoader && (
													<CircularProgress
														size={24}
														sx={{
															color: '#004366',
															position: 'absolute',
															top: '50%',
															left: '50%',
															marginTop: '-12px',
															marginLeft: '-12px',
														}}
													/>
												)}
											</Button>
										)}
									</>
								)}
							</Box>
						</Box>
					)}
					{!orderError && (orderIsLoading || String(order?.order.id) !== orderId) && (
						<Box sx={{ mb: 2 }}>
							<Skeleton variant="rectangular" height="80px" sx={{ borderRadius: '12px' }} />
						</Box>
					)}
					{!orderError && (
						<Grid container spacing={2}>
							<Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
								<OrderDetails />
								<ShipmentDetails />
							</Grid>

							<Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
								<DeliveryInformation />
								<ShipmentTimeLine
									handleRequestDelivery={handleRequestDeliveryDialogOpen}
									handleUploadProForma={handleOpenUploadProFormaDialog}
								/>
								<AmountBreakdown />
							</Grid>
						</Grid>
					)}
				</Box>
			</Box>
			<ConfirmationDialog
				open={requestDeliveryDialogOpen}
				setClose={handleRequestDeliveryDialogClose}
				handleConfirm={handleRequestDeliveryDiscount}
				title="Request Swiss Post Delivery"
				text="Confirm Swiss Post DDP delivery request?<br />
				You will be invoiced once the downloaded label has been used for delivery."
			/>
			<NoticeDialog
				open={firstMileLabelErrorDialogOpen}
				setClose={handleFirstMileLabelErrorDialogClose}
				title="We are not able to generate first mile label for your shipment"
			>
				<Box>
					<Typography variant="body1" gutterBottom>
						Your Franking license number is missing. Follow{' '}
						<Link
							href="https://help.easproject.com/how-to-find-your-franking-number-in-your-swiss-post-account"
							target="_blank"
							rel="noreferrer"
						>
							this guidance
						</Link>{' '}
						to find your Franking license number
					</Typography>
				</Box>
			</NoticeDialog>
			<UploadProFormaDialog
				open={uploadDialogOpen}
				setClose={handleUploadDialogClose}
				handleConfirm={handleUploadInvoiceProforma}
				title={
					order?.order.schemes === 'IOSS-IMPORT'
						? 'Please, upload IOSS supportive document'
						: 'Please upload the signed Pro-forma Invoice'
				}
			>
				{order?.order.schemes === 'IOSS-IMPORT' ? (
					<Box>
						<Typography variant="body1" gutterBottom>
							You need to upload a document that confirms the value of the goods in the order. It
							can be a commercial B2C invoice or order confirmation printed out from your store.
						</Typography>
					</Box>
				) : (
					<Box>
						<Typography variant="body1" gutterBottom>
							Sign and upload the pro forma invoice that you downloaded from EAS dashboard. This
							document is required for customs clearance.
						</Typography>
						<Typography variant="body1" gutterBottom>
							Ensure that proper signature is attached, you will not be able to change or amend the
							proforma.
						</Typography>
					</Box>
				)}
			</UploadProFormaDialog>
			{/* On-screen signing:
			{order && (
				<SignProFormaDialog
					open={signDialogOpen}
					setClose={handleSignDialogClose}
					orderId={order.order.id}
					externalOrderId={order.order.externalOrderId}
				/>
			)} */}
		</Box>
	);
};
